/**
 * Every non-2xx response becomes one of these, so callers can branch on
 * `instanceof` instead of poking at status codes and response shapes.
 */
export class ApiError extends Error {
  readonly status: number
  readonly payload: unknown

  constructor(status: number, message: string, payload: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }

  static from(status: number, payload: unknown): ApiError {
    const message = messageOf(payload) ?? `Request failed with status ${status}.`

    if (status === 403 && typeof payload === 'object' && payload !== null && 'code' in payload && typeof payload.code === 'string' && payload.code.startsWith('entitlement_')) {
      return new EntitlementError(message, payload)
    }

    if (status === 422 && isValidationPayload(payload)) {
      return new ValidationError(message, payload.errors, payload)
    }

    return new ApiError(status, message, payload)
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  /**
   * The API's machine-readable reason, when it gives one — "session_expired",
   * "step_up_required", "stale" — so callers branch on it rather than on
   * the (localized) message.
   */
  get code(): string | undefined {
    const { payload } = this
    if (typeof payload === 'object' && payload !== null && 'code' in payload) {
      const { code } = payload as { code: unknown }
      return typeof code === 'string' ? code : undefined
    }

    return undefined
  }
}

export class EntitlementError extends ApiError {
  readonly hint: string
  constructor(message: string, payload: object) {
    super(403, message, payload)
    this.name = 'EntitlementError'
    this.hint = 'hint' in payload && typeof payload.hint === 'string' ? payload.hint : ''
  }
}

/**
 * Laravel's 422 shape: { message, errors: { field: [messages] } }.
 */
export class ValidationError extends ApiError {
  readonly errors: Readonly<Record<string, readonly string[]>>

  /** `payload` is the API's own body, so its `code` and `details` stay readable beside the field errors. */
  constructor(message: string, errors: Record<string, string[]>, payload?: object) {
    super(422, message, payload ?? { message, errors })
    this.name = 'ValidationError'
    this.errors = errors
  }

  /** First message for a field — what a form shows under the input. */
  first(field: string): string | undefined {
    return this.errors[field]?.[0]
  }
}

function messageOf(payload: unknown): string | undefined {
  if (typeof payload === 'object' && payload !== null && 'message' in payload) {
    const { message } = payload as { message: unknown }
    return typeof message === 'string' && message !== '' ? message : undefined
  }

  return undefined
}

function isValidationPayload(payload: unknown): payload is { errors: Record<string, string[]> } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'errors' in payload &&
    typeof (payload as { errors: unknown }).errors === 'object'
  )
}

/**
 * The platform is under maintenance at this company's address (P16): 503
 * with `{message, maintenance: {mode, ends_at}}`. The message is already in
 * the language asked for.
 */
export function isMaintenance(error: unknown): error is ApiError {
  if (!(error instanceof ApiError) || error.status !== 503) return false
  const { payload } = error
  return typeof payload === 'object' && payload !== null && 'maintenance' in payload && typeof (payload as { maintenance: unknown }).maintenance === 'object'
}

export interface MaintenanceNotice {
  mode: 'full' | 'read_only'
  ends_at: string
  message: string
}

/** The maintenance a 503 announced, or null for any other error. */
export function maintenanceOf(error: unknown): MaintenanceNotice | null {
  if (!isMaintenance(error)) return null
  const payload = error.payload as { message?: unknown; maintenance: { mode?: unknown; ends_at?: unknown } }

  return {
    mode: payload.maintenance.mode === 'read_only' ? 'read_only' : 'full',
    ends_at: typeof payload.maintenance.ends_at === 'string' ? payload.maintenance.ends_at : '',
    message: typeof payload.message === 'string' ? payload.message : error.message,
  }
}
